import React, { useEffect, Suspense } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { motion, AnimatePresence } from 'framer-motion'

// Layout components
import Navigation from './components/layout/Navigation'
import Footer from './components/layout/Footer'
import NotificationContainer from './components/ui/NotificationContainer'
import GlobalLoader from './components/ui/GlobalLoader'
import ScrollToTop from './components/ui/ScrollToTop'
import ReadingProgress from './components/ui/ReadingProgress'
import ErrorBoundary from './components/ui/ErrorBoundary'
import UpdateNotification from './components/ui/UpdateNotification'
import LoadingState from './components/ui/LoadingState'

// Lazy loaded pages
import {
  LazyHome,
  LazyAbout,
  LazyContact,
  LazyPortfolio,
  LazyResume,
  LazyLogin,
  LazyAdminDashboard,
  LazyAdminProjects,
  LazyAdminSkills,
  LazyAdminProfile,
  LazyAdminMessages,
  LazyAdminResume,
  LazyNotFound
} from './components/ui/LazyComponent'

// Admin layout (не lazy, так как критичен)
import AdminLayout from './pages/admin/AdminLayout'

// Store actions
import { initializeApp } from './store/slices/appSlice'
import { checkAuth } from './store/slices/authSlice'

// Route guards
import ProtectedRoute from './components/auth/ProtectedRoute'
import GuestRoute from './components/auth/GuestRoute'

// Performance utilities
import { startPerformanceMonitoring } from './utils/performance'
import { register as registerSW } from './utils/serviceWorker'

function App() {
  const dispatch = useDispatch()
  const location = useLocation()
  const { isLoading } = useSelector(state => state.app)
  
  const isAdminRoute = location.pathname.startsWith('/admin')
  
  useEffect(() => {
    // Инициализация приложения
    dispatch(initializeApp())
    dispatch(checkAuth())
    
    // Запуск мониторинга производительности
    if (import.meta.env.NODE_ENV === 'development') {
      startPerformanceMonitoring()
    }
    
    // Регистрация Service Worker
    if (import.meta.env.NODE_ENV === 'production') {
      registerSW({
        onSuccess: (registration) => {
          console.log('SW registered successfully')
        },
        onUpdate: (registration) => {
          console.log('SW update available')
        }
      })
    }
  }, [])
  
  return (
    <ErrorBoundary>
      <div className={`app ${isAdminRoute ? 'admin-mode' : ''}`}>
        {/* Индикатор прогресса чтения */}
        {!isAdminRoute && <ReadingProgress />}
        
        {/* Публичная навигация */}
        {!isAdminRoute && <Navigation />}
        
        {/* Основной контент с анимациями */}
        <main className="main-content">
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              {/* Публичные маршруты */}
              <Route 
                path="/" 
                element={
                  <PageTransition>
                    <Suspense fallback={<LoadingState />}>
                      <LazyHome />
                    </Suspense>
                  </PageTransition>
                } 
              />
              <Route 
                path="/about" 
                element={
                  <PageTransition>
                    <Suspense fallback={<LoadingState />}>
                      <LazyAbout />
                    </Suspense>
                  </PageTransition>
                } 
              />
              <Route 
                path="/contact" 
                element={
                  <PageTransition>
                    <Suspense fallback={<LoadingState />}>
                      <LazyContact />
                    </Suspense>
                  </PageTransition>
                } 
              />
              <Route
                path="/portfolio"
                element={
                  <PageTransition>
                    <Suspense fallback={<LoadingState />}>
                      <LazyPortfolio />
                    </Suspense>
                  </PageTransition>
                }
              />
              <Route
                path="/resume"
                element={
                  <PageTransition>
                    <Suspense fallback={<LoadingState />}>
                      <LazyResume />
                    </Suspense>
                  </PageTransition>
                }
              />
              
              {/* Админ логин */}
              <Route 
                path="/admin/login" 
                element={
                  <GuestRoute>
                    <PageTransition>
                      <Suspense fallback={<LoadingState />}>
                        <LazyLogin />
                      </Suspense>
                    </PageTransition>
                  </GuestRoute>
                } 
              />
              
              {/* Админ панель */}
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route 
                  index 
                  element={
                    <Suspense fallback={<LoadingState />}>
                      <LazyAdminDashboard />
                    </Suspense>
                  } 
                />
                <Route 
                  path="projects" 
                  element={
                    <Suspense fallback={<LoadingState />}>
                      <LazyAdminProjects />
                    </Suspense>
                  } 
                />
                <Route 
                  path="skills" 
                  element={
                    <Suspense fallback={<LoadingState />}>
                      <LazyAdminSkills />
                    </Suspense>
                  } 
                />
                <Route 
                  path="profile" 
                  element={
                    <Suspense fallback={<LoadingState />}>
                      <LazyAdminProfile />
                    </Suspense>
                  } 
                />
                <Route 
                  path="messages" 
                  element={
                    <Suspense fallback={<LoadingState />}>
                      <LazyAdminMessages />
                    </Suspense>
                  } 
                />
                <Route 
                  path="resume" 
                  element={
                    <Suspense fallback={<LoadingState />}>
                      <LazyAdminResume />
                    </Suspense>
                  } 
                />
              </Route>
              
              {/* 404 */}
              <Route 
                path="*" 
                element={
                  <PageTransition>
                    <Suspense fallback={<LoadingState />}>
                      <LazyNotFound />
                    </Suspense>
                  </PageTransition>
                } 
              />
            </Routes>
          </AnimatePresence>
        </main>
        
        {/* Футер только для публичных страниц */}
        {!isAdminRoute && <Footer />}
        
        {/* Глобальные компоненты */}
        <NotificationContainer />
        <ScrollToTop />
        <UpdateNotification />
        {isLoading && <GlobalLoader />}
      </div>
    </ErrorBoundary>
  )
}

// Компонент для анимации переходов между страницами
const PageTransition = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  )
}

export default App